/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import React from 'react'
import { DATA_GRID_TOKENS as T } from 'src/components/ts-components/DataGrid/tokens'
import GridIcon from '../icons/GridIcon'

interface RowExpandToggleProps {
  expanded: boolean
  onToggle: () => void
  /**
   * Accessible name: a team row says "Expand row" / "Collapse row", a division
   * bar "Expand division" / "Collapse division".
   */
  label: string
  /** Nothing to expand: the toggle shows but does not act. */
  disabled?: boolean
}

/**
 * AG Grid's expand chevron (Quartz `tree-closed` / `tree-open`) as a plain
 * button: pointing right while collapsed, down while expanded.
 */
const RowExpandToggle: React.FC<RowExpandToggleProps> = ({
  expanded,
  onToggle,
  label,
  disabled = false,
}) => {
  'use memo'

  return (
    <button
      type='button'
      css={toggleStyles}
      aria-expanded={expanded}
      aria-label={label}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation()
        onToggle()
      }}
    >
      <GridIcon name={expanded ? 'tree-open' : 'tree-closed'} />
    </button>
  )
}

export default RowExpandToggle

const toggleStyles = css`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: ${T.iconSize}px;
  height: ${T.iconSize}px;
  padding: 0;
  color: ${colors.NEUTRAL_50};
  background: none;
  border: 0;
  border-radius: ${T.checkbox.radius}px;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${T.headerText};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${T.focusShadow};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`
